import Grid from '@mui/material/Grid';
import * as Yup from 'yup';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { Alert, Button, Snackbar } from '@mui/material';
import { useSaveData } from '../../hooks/useSaveData';
import { ContractForm } from './contract.form';
import { Contract } from '../../models/contract';
import { contractApiKeyStore } from '../../api/contract-api-key-store';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useParams } from 'react-router-dom';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface CustomerForm {
  contract?: Contract;
  onSaveContract: (contract: Contract) => void;
}

const contractValidationSchema = Yup.object().shape({
  contractName: Yup.string().required('Contract name is required'),
  contractStartDate: Yup.string().required('Start date is required'),
  contractEndDate: Yup.string().required('End date is required'),
});

export const SaveContactComponent = ({ contract, onSaveContract }: CustomerForm) => {
  const [files, setFiles] = useState<File>();
  const [open, setOpen] = useState(false);
  const { officeID } = useParams();
  const form = useReactHookForm({ defaultValues: contract, yupSchema: contractValidationSchema });
  const { isLoading, mutateAsync } = useSaveData<Contract>(
    contract?.contractID ? contractApiKeyStore.updateContract() : contractApiKeyStore.createContract()
  );

  const contractID = contract?.contractID?.toString() || '';

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const handleSubmit = async (value: any): Promise<any> => {
    if (files || contract?.fileName) {
      const formData = new FormData();
      formData.append('UploadedFile', files || '');
      formData.append('FileName', files?.name || '');
      formData.append('FileType', files?.type || '');

      formData.append('contractID', contractID);
      formData.append('Description', value.description ?? '');
      formData.append('FileSection', value.projectID);
      formData.append('ContractName', value.contractName);
      formData.append('ContractStartDate', value.contractStartDate);
      formData.append('ContractEndDate', value.contractEndDate);
      formData.append('OfficeID', officeID ?? '');
      const data = await mutateAsync({ data: formData });
      onSaveContract(data?.data ?? []);
      form.reset({ fileUpload: [], description: '' });
    } else {
      setOpen(true);
    }
  };

  const handleSubmitClick = () => {
    form.handleSubmit(handleSubmit)();
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="SAVE_CONTARCT_FORM" onSubmit={handleSubmit} formContextProps={form} isBusy={isLoading}>
      <Grid container spacing={2}>
        <ContractForm
          files={files}
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          contract={contract}
        />
        <Grid container justifyContent="flex-end" spacing={1} style={styling.buttonPadding}>
          <Grid item>
            <Button
              type="button"
              disableElevation={true}
              variant="contained"
              style={styling.buttonStyle}
              disabled={isLoading || isReadOnlyPermission}
              onClick={handleSubmitClick}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleClose}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
            Please select the file
          </Alert>
        </Snackbar>
      </Grid>
    </ReactHookForm>
  );
};
